<template>
    <div class="flex flex-col h-full">
        <!-- 可滚动内容区域 -->
        <div class="flex-1 overflow-y-auto space-y-6">
            <!-- 标签来源配置 -->
            <div class="space-y-4">
                <div class="flex items-center space-x-3">
                    <div class="w-1 h-5 bg-primary rounded-full"></div>
                    <h3 class="text-lg font-semibold">{{ $tt('标签来源') }}</h3>
                    <div class="badge badge-primary badge-sm">{{ $tt('基础配置') }}</div>
                </div>

                <div class="space-y-3 ml-4 pl-3 border-l border-base-200">
                    <div class="form-control w-full">
                        <label class="label">
                            <span class="label-text font-medium">{{ $tt('选择元数据来源') }}</span>
                            <span class="label-text-alt text-base-content/60">{{ $tt('默认：网易云音乐') }}</span>
                        </label>
                        <single-select
                            :options="resourceList"
                            v-model="configData.resource"
                            :placeholder="$tt('选择音乐元数据来源')"
                        />
                    </div>
                </div>
            </div>

            <!-- 字段分组配置 -->
            <div class="space-y-4">
                <div class="flex items-center justify-between">
                    <div class="flex items-center space-x-3">
                        <div class="w-1 h-5 bg-secondary rounded-full"></div>
                        <h3 class="text-lg font-semibold">{{ $tt('字段分组管理') }}</h3>
                        <div class="badge badge-secondary badge-sm">{{ $tt('高级配置') }}</div>
                    </div>
                    <button class="btn btn-sm btn-outline btn-primary" @click="addNewGroup">
                        <svg-icon name="plus" class="w-4 h-4 mr-1" />
                        {{ $tt('新建分组') }}
                    </button>
                </div>

                <!-- 分组列表 -->
                <div class="space-y-3">
                    <div v-for="(group, groupIndex) in configData.fieldGroups" :key="group.id"
                        class="bg-base-200 border border-base-300 rounded-lg p-4 space-y-3 transition-all duration-200"
                        :class="{ 'opacity-50': draggedGroupIndex === groupIndex }"
                        draggable="true"
                        @dragstart="handleGroupDragStart($event, groupIndex)"
                        @dragover="handleGroupDragOver($event)"
                        @drop="handleGroupDrop($event, groupIndex)"
                        @dragend="handleGroupDragEnd">

                        <!-- 分组标题 -->
                        <div class="flex items-center justify-between">
                            <div class="flex items-center space-x-3">
                                <div class="cursor-move opacity-50 hover:opacity-100 transition-opacity">
                                    <svg-icon name="grip-vertical" class="w-4 h-4" />
                                </div>
                                <input
                                    v-model="group.name"
                                    class="input input-sm input-bordered flex-1 max-w-[150px] font-medium"
                                    :placeholder="$tt('分组名称')"
                                />
                                <div class="badge badge-ghost badge-sm">{{ $tt('{count} 个字段', { count: group.fields.length }) }}</div>
                                <div v-if="group.isCustom" class="badge badge-warning badge-xs">{{ $tt('自定义') }}</div>
                            </div>
                            <div class="flex space-x-1">
                                <button class="btn btn-ghost btn-xs btn-square" @click="moveGroupUp(groupIndex)" :disabled="groupIndex === 0">
                                    <svg-icon name="chevron-up" class="w-3 h-3" />
                                </button>
                                <button class="btn btn-ghost btn-xs btn-square" @click="moveGroupDown(groupIndex)" :disabled="groupIndex === configData.fieldGroups.length - 1">
                                    <svg-icon name="chevron-down" class="w-3 h-3" />
                                </button>
                                <button v-if="group.isCustom" class="btn btn-ghost btn-xs btn-square text-error" @click="removeGroup(groupIndex)">
                                    <svg-icon name="trash" class="w-3 h-3" />
                                </button>
                            </div>
                        </div>

                        <!-- 字段列表 -->
                        <div class="space-y-2">
                            <div class="flex items-center justify-between">
                                <span class="text-sm font-medium text-base-content/70">{{ $tt('字段列表') }}</span>
                                <div class="flex space-x-1">
                                    <button class="btn btn-ghost btn-xs" @click="openFieldSelector(groupIndex)">
                                        <svg-icon name="plus" class="w-3 h-3 mr-1" />
                                        {{ $tt('添加字段') }}
                                    </button>
                                    <button class="btn btn-ghost btn-xs" @click="clearGroupFields(groupIndex)">
                                        {{ $tt('清空') }}
                                    </button>
                                </div>
                            </div>

                            <div
                                class="flex flex-wrap gap-2 min-h-[40px] p-2 bg-base-100 rounded border border-base-200 transition-colors"
                                :class="{ 'border-primary border-2 bg-primary/5': draggedFieldGroupIndex === groupIndex }"
                                @dragover="handleFieldDragOver($event)"
                                @drop="handleFieldDropOnGroup($event, groupIndex)">
                                <div v-for="(field, fieldIndex) in group.fields" :key="field"
                                    class="badge badge-outline badge-lg cursor-move group relative transition-all duration-200"
                                    :class="{
                                        'opacity-50': draggedFieldGroupIndex === groupIndex && draggedFieldIndex === fieldIndex,
                                        'scale-95 shadow-lg': draggedFieldGroupIndex === groupIndex && draggedFieldIndex === fieldIndex
                                    }"
                                    draggable="true"
                                    @dragstart="handleFieldDragStart($event, groupIndex, fieldIndex)"
                                    @dragover="handleFieldInnerDragOver($event)"
                                    @drop="handleFieldInnerDrop($event, groupIndex, fieldIndex)"
                                    @dragend="handleFieldDragEnd">
                                    <span class="flex items-center space-x-1">
                                        <svg-icon name="grip-vertical" class="w-3 h-3 opacity-50" />
                                        <span>{{ getFieldDisplayName(field) }}</span>
                                        <button @click="removeFieldFromGroup(groupIndex, fieldIndex)"
                                            class="ml-1 opacity-0 group-hover:opacity-100 transition-opacity hover:text-error">
                                            <svg-icon name="x" class="w-3 h-3" />
                                        </button>
                                    </span>
                                </div>
                                <div v-if="group.fields.length === 0" class="text-sm text-base-content/40 italic w-full text-center py-2">
                                    {{ $tt('拖拽或点击添加字段到分组') }}
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </div>

        <!-- 固定在底部的操作按钮 -->
        <div class="sticky bottom-0 flex items-center justify-between p-4 border-t border-base-200 bg-base-100 shadow-lg">
            <div class="flex space-x-3">
                <button class="btn btn-sm btn-primary" @click="handleSaveConfig" :disabled="isSaving">
                    <span class="loading loading-spinner loading-xs" v-if="isSaving"></span>
                    {{ $tt('保存配置') }}
                </button>
                <button class="btn btn-sm btn-ghost" @click="$emit('close')">{{ $tt('关闭') }}</button>
            </div>
            <!-- <div class="flex space-x-3 text-xs">
                <button @click="exportConfig" class="link link-primary">{{ $tt('导出配置') }}</button>
                <button @click="importConfig" class="link link-primary">{{ $tt('导入配置') }}</button>
                <button @click="resetConfig" class="link link-error">{{ $tt('重置默认') }}</button>
            </div> -->
        </div>

        <!-- 字段选择器弹窗 -->
        <dialog ref="fieldSelectorModal" class="modal modal-bottom sm:modal-middle">
            <div class="modal-box max-w-md">
                <h3 class="font-bold text-lg">{{ $tt('选择字段') }}</h3>
                <div class="py-4">
                    <div class="form-control">
                        <input
                            v-model="fieldSearchKeyword"
                            type="text"
                            :placeholder="$tt('搜索字段...')"
                            class="input input-bordered input-sm mb-3"
                        />
                    </div>
                    <div class="max-h-60 overflow-y-auto space-y-2">
                        <div v-for="field in filteredFieldList" :key="field.id"
                            @click="addFieldToSelectedGroup(field.id)"
                            class="p-2 rounded hover:bg-base-200 cursor-pointer transition-colors">
                            <div class="flex items-center justify-between">
                                <span class="font-medium">{{ field.name }}</span>
                                <span class="text-xs text-base-content/60">{{ field.id }}</span>
                            </div>
                        </div>
                    </div>
                </div>
                <div class="modal-action">
                    <form method="dialog">
                        <button class="btn btn-sm">{{ $tt('取消') }}</button>
                    </form>
                </div>
            </div>
        </dialog>

        <!-- 隐藏的文件输入用于导入配置 -->
        <input ref="configFileInput" type="file" accept=".json" class="hidden" @change="handleConfigFileImport" />
    </div>
</template>

<script>
import SingleSelect from "@/components/iview/SingleSelect"
import MultiSelect from "@/components/iview/MultiSelect"
import SvgIcon from '@/components/SvgIcon.vue'

export default {
    name: 'ScrapingSettingsContent',
    components: {
        SingleSelect,
        MultiSelect,
        SvgIcon
    },
    data() {
        return {
            isSaving: false,
            isLoading: false,
            fieldSearchKeyword: '',
            selectedGroupIndex: null,

            // 拖拽状态
            draggedGroupIndex: null,
            draggedFieldGroupIndex: null,
            draggedFieldIndex: null,
            draggedFieldData: null,

            // 字段列表
            fieldList: [
                { id: 'subtitle', name: this.$tt('副标题') },
                { id: 'filename', name: this.$tt('文件名') },
                { id: 'artist', name: this.$tt('艺术家') },
                { id: 'album', name: this.$tt('专辑') },
                { id: 'album_type', name: this.$tt('专辑类型') },
                { id: 'albumartist', name: this.$tt('专辑艺术家') },
                { id: 'discnumber', name: this.$tt('光盘编号') },
                { id: 'tracknumber', name: this.$tt('音轨号') },
                { id: 'totaltracks', name: this.$tt('总音轨数') },
                { id: 'totaldiscs', name: this.$tt('总光盘数') },
                { id: 'genre', name: this.$tt('风格') },
                { id: 'year', name: this.$tt('年份') },
                { id: 'date', name: this.$tt('日期') },
                { id: 'lyrics', name: this.$tt('歌词') },
                { id: 'lyrics_file', name: this.$tt('歌词(LRC文件)') },
                { id: 'comment', name: this.$tt('简介') },
                { id: 'description', name: this.$tt('描述') },
                { id: 'albumversion', name: this.$tt('专辑版本') },
                { id: 'album_img', name: this.$tt('专辑封面') },
                { id: 'duration', name: this.$tt('时长') },
                { id: 'size', name: this.$tt('文件大小') },
                { id: 'bit_rate', name: this.$tt('比特率') },
                { id: 'sample_rate', name: this.$tt('采样率') },
                { id: 'channels', name: this.$tt('声道') },
                { id: 'bit_depth', name: this.$tt('位深度') },
                { id: 'language', name: this.$tt('语言') },
                { id: 'play', name: this.$tt('试听') },
                { id: 'composer', name: this.$tt('作曲家') },
                { id: 'lyricist', name: this.$tt('作词家') },
                { id: 'replaygain_track_gain', name: this.$tt('曲目增益') },
                { id: 'replaygain_track_peak', name: this.$tt('曲目峰值') },
                { id: 'replaygain_album_gain', name: this.$tt('专辑增益') },
                { id: 'replaygain_album_peak', name: this.$tt('专辑峰值') },
                { id: 'label', name: this.$tt('标签') },
                { id: 'id3_version', name: this.$tt('元数据版本') }
            ],

            // 配置数据
            configData: {
                resource: 'netease',
                fieldGroups: []
            },

            // 资源列表
            resourceList: [],

            // 默认配置
            defaultFieldGroups: [
                {
                    id: 'basic',
                    name: this.$tt('基础信息'),
                    fields: ['filename', 'artist', 'album', 'albumartist', 'year', 'date'],
                    isCustom: false
                },
                {
                    id: 'category',
                    name: this.$tt('分类信息'),
                    fields: ['genre', 'language', 'label', 'album_type'],
                    isCustom: false
                },
                {
                    id: 'lyrics',
                    name: this.$tt('歌词信息'),
                    fields: ['lyrics', 'lyrics_file'],
                    isCustom: false
                },
                {
                    id: 'metadata',
                    name: this.$tt('元数据'),
                    fields: ['comment', 'description', 'composer', 'lyricist', 'albumversion'],
                    isCustom: false
                },
                {
                    id: 'media',
                    name: this.$tt('媒体文件'),
                    fields: ['album_img', 'play'],
                    isCustom: false
                },
                {
                    id: 'technical',
                    name: this.$tt('技术参数'),
                    fields: ['discnumber', 'tracknumber', 'totaltracks', 'totaldiscs', 'duration', 'bit_rate', 'sample_rate', 'bit_depth', 'channels', 'size', 'id3_version'],
                    isCustom: false
                },
                {
                    id: 'audio',
                    name: this.$tt('音频增益'),
                    fields: ['replaygain_track_gain', 'replaygain_track_peak', 'replaygain_album_gain', 'replaygain_album_peak'],
                    isCustom: false
                }
            ]
        }
    },
    computed: {
        filteredFieldList() {
            if (!this.fieldSearchKeyword) {
                return this.fieldList
            }
            return this.fieldList.filter(field =>
                field.name.toLowerCase().includes(this.fieldSearchKeyword.toLowerCase()) ||
                field.id.toLowerCase().includes(this.fieldSearchKeyword.toLowerCase())
            )
        }
    },
    created() {
        this.loadConfig()
        this.loadTagSourcesFromStore()
    },
    methods: {
        // 加载配置
        loadConfig() {
            try {
                const savedConfig = localStorage.getItem('scrapingSettings')
                if (savedConfig) {
                    const parsed = JSON.parse(savedConfig)
                    this.configData = {
                        resource: parsed.resource || 'netease',
                        fieldGroups: parsed.fieldGroups || [...this.defaultFieldGroups]
                    }
                } else {
                    // 使用默认配置
                    this.configData = {
                        resource: 'netease',
                        fieldGroups: [...this.defaultFieldGroups]
                    }
                }
            } catch (error) {
                console.error('加载配置失败:', error)
                this.configData = {
                    resource: 'netease',
                    fieldGroups: [...this.defaultFieldGroups]
                }
            }
        },

        // 保存配置
        async handleSaveConfig() {
            this.isSaving = true
            try {
                const configToSave = {
                    resource: this.configData.resource,
                    fieldGroups: this.configData.fieldGroups,
                    version: '1.0.0',
                    timestamp: new Date().toISOString()
                }

                localStorage.setItem('scrapingSettings', JSON.stringify(configToSave))

                // 向父组件发送配置更新事件
                this.$emit('config-updated', configToSave)

                this.$myMsg.notify({
                    title: this.$tt('保存成功！'),
                    content: this.$tt('刮削配置已保存'),
                    type: 'success',
                    time: 2000,
                })

                // 保存成功后关闭弹窗
                this.$emit('close')
            } catch (error) {
                console.error('保存配置失败:', error)
                this.$myMsg.notify({
                    title: this.$tt('保存失败！'),
                    content: error.message,
                    type: 'error',
                    time: 3000,
                })
            } finally {
                this.isSaving = false
            }
        },

        // 从 Vuex store 加载标签源列表
        async loadTagSourcesFromStore() {
            try {
                // 从 Vuex store 加载标签源（带缓存）
                const sources = await this.$store.dispatch('loadTagSources')
                this.resourceList = sources

                // 检查当前选中的资源是否还存在于新的列表中
                const availableResourceIds = this.resourceList.map(item => item.id)
                if (!availableResourceIds.includes(this.configData.resource)) {
                    // 如果当前选中的资源不存在,设置为列表中的第一个
                    if (this.resourceList.length > 0) {
                        this.configData.resource = this.resourceList[0].id
                    }
                }
            } catch (error) {
                console.error('加载标签源失败:', error)
            }
        },


        
        // 获取字段显示名称
        getFieldDisplayName(fieldId) {
            const field = this.fieldList.find(f => f.id === fieldId)
            return field ? field.name : fieldId
        },

        // 添加新分组
        addNewGroup() {
            const newGroup = {
                id: `custom_${Date.now()}`,
                name: this.$tt('新分组'),
                fields: [],
                isCustom: true
            }
            this.configData.fieldGroups.push(newGroup)
        },

        // 删除分组
        removeGroup(index) {
            if (confirm(this.$tt('确定要删除这个分组吗？分组中的字段不会被删除。'))) {
                this.configData.fieldGroups.splice(index, 1)
            }
        },

        // 上移分组
        moveGroupUp(index) {
            if (index > 0) {
                const group = this.configData.fieldGroups.splice(index, 1)[0]
                this.configData.fieldGroups.splice(index - 1, 0, group)
            }
        },

        // 下移分组
        moveGroupDown(index) {
            if (index < this.configData.fieldGroups.length - 1) {
                const group = this.configData.fieldGroups.splice(index, 1)[0]
                this.configData.fieldGroups.splice(index + 1, 0, group)
            }
        },

        // 打开字段选择器
        openFieldSelector(groupIndex) {
            this.selectedGroupIndex = groupIndex
            this.fieldSearchKeyword = ''
            this.$refs.fieldSelectorModal.showModal()
        },

        // 添加字段到分组
        addFieldToSelectedGroup(fieldId) {
            if (this.selectedGroupIndex !== null) {
                const group = this.configData.fieldGroups[this.selectedGroupIndex]
                if (!group.fields.includes(fieldId)) {
                    group.fields.push(fieldId)
                }
                this.$refs.fieldSelectorModal.close()
            }
        },

        // 从分组中移除字段
        removeFieldFromGroup(groupIndex, fieldIndex) {
            this.configData.fieldGroups[groupIndex].fields.splice(fieldIndex, 1)
        },

        // 清空分组字段
        clearGroupFields(groupIndex) {
            if (confirm(this.$tt('确定要清空这个分组的所有字段吗？'))) {
                this.configData.fieldGroups[groupIndex].fields = []
            }
        },

        
        // 导出配置
        exportConfig() {
            try {
                const configData = {
                    ...this.configData,
                    exportTime: new Date().toISOString(),
                    version: '1.0.0'
                }

                const blob = new Blob([JSON.stringify(configData, null, 2)], { type: 'application/json' })
                const url = URL.createObjectURL(blob)
                const a = document.createElement('a')
                a.href = url
                a.download = `scraping-settings-${new Date().toISOString().split('T')[0]}.json`
                document.body.appendChild(a)
                a.click()
                document.body.removeChild(a)
                URL.revokeObjectURL(url)

                this.$myMsg.notify({
                    title: this.$tt('导出成功！'),
                    content: this.$tt('配置文件已下载'),
                    type: 'success',
                    time: 2000,
                })
            } catch (error) {
                console.error('导出配置失败:', error)
                this.$myMsg.notify({
                    title: this.$tt('导出失败！'),
                    content: error.message,
                    type: 'error',
                    time: 3000,
                })
            }
        },

        // 导入配置
        importConfig() {
            this.$refs.configFileInput.click()
        },

        // 处理配置文件导入
        handleConfigFileImport(event) {
            const file = event.target.files[0]
            if (!file) return

            const reader = new FileReader()
            reader.onload = (e) => {
                try {
                    const importedConfig = JSON.parse(e.target.result)

                    // 验证配置格式
                    if (importedConfig.fieldGroups && Array.isArray(importedConfig.fieldGroups)) {
                        this.configData = {
                            resource: importedConfig.resource || 'netease',
                            fieldGroups: importedConfig.fieldGroups
                        }

                        this.$myMsg.notify({
                            title: this.$tt('导入成功！'),
                            content: this.$tt('配置已导入，请检查并保存'),
                            type: 'success',
                            time: 2000,
                        })
                    } else {
                        throw new Error(this.$tt('配置文件格式不正确'))
                    }
                } catch (error) {
                    console.error('导入配置失败:', error)
                    this.$myMsg.notify({
                        title: this.$tt('导入失败！'),
                        content: error.message,
                        type: 'error',
                        time: 3000,
                    })
                }
            }
            reader.readAsText(file)

            // 清空文件输入
            event.target.value = ''
        },

        // 重置配置
        resetConfig() {
            if (confirm(this.$tt('确定要重置为默认配置吗？当前配置将丢失。'))) {
                this.configData = {
                    resource: 'netease',
                    fieldGroups: [...this.defaultFieldGroups]
                }

                this.$myMsg.notify({
                    title: this.$tt('重置成功！'),
                    content: this.$tt('已恢复为默认配置，请保存'),
                    type: 'info',
                    time: 2000,
                })
            }
        },

        // ===== 拖拽功能方法 =====

        // 分组拖拽开始
        handleGroupDragStart(event, groupIndex) {
            this.draggedGroupIndex = groupIndex
            event.dataTransfer.effectAllowed = 'move'
            event.dataTransfer.setData('text/plain', groupIndex.toString())
        },

        // 分组拖拽经过
        handleGroupDragOver(event) {
            event.preventDefault()
            event.dataTransfer.dropEffect = 'move'
        },

        // 分组放置
        handleGroupDrop(event, targetGroupIndex) {
            event.preventDefault()
            if (this.draggedGroupIndex === null || this.draggedGroupIndex === targetGroupIndex) {
                return
            }

            const draggedGroup = this.configData.fieldGroups[this.draggedGroupIndex]
            this.configData.fieldGroups.splice(this.draggedGroupIndex, 1)

            const newInsertIndex = this.draggedGroupIndex < targetGroupIndex ? targetGroupIndex - 1 : targetGroupIndex
            this.configData.fieldGroups.splice(newInsertIndex, 0, draggedGroup)
        },

        // 分组拖拽结束
        handleGroupDragEnd() {
            this.draggedGroupIndex = null
        },

        // 字段拖拽开始
        handleFieldDragStart(event, groupIndex, fieldIndex) {
            this.draggedFieldGroupIndex = groupIndex
            this.draggedFieldIndex = fieldIndex
            this.draggedFieldData = this.configData.fieldGroups[groupIndex].fields[fieldIndex]

            event.dataTransfer.effectAllowed = 'move'
            event.dataTransfer.setData('text/plain', this.draggedFieldData)
        },

        // 字段拖拽经过组内
        handleFieldInnerDragOver(event) {
            event.preventDefault()
            event.dataTransfer.dropEffect = 'move'
        },

        // 字段组内放置（排序）
        handleFieldInnerDrop(event, groupIndex, targetFieldIndex) {
            event.preventDefault()

            // 确保是同一分组内的拖拽
            if (this.draggedFieldGroupIndex !== groupIndex ||
                this.draggedFieldIndex === null ||
                this.draggedFieldIndex === targetFieldIndex) {
                return
            }

            const group = this.configData.fieldGroups[groupIndex]
            const draggedField = group.fields.splice(this.draggedFieldIndex, 1)[0]

            const newInsertIndex = this.draggedFieldIndex < targetFieldIndex ? targetFieldIndex - 1 : targetFieldIndex
            group.fields.splice(newInsertIndex, 0, draggedField)
        },

        // 字段拖拽经过分组
        handleFieldDragOver(event) {
            event.preventDefault()
            event.dataTransfer.dropEffect = 'move'
        },

        // 字段放置到分组（跨分组移动）
        handleFieldDropOnGroup(event, targetGroupIndex) {
            event.preventDefault()

            if (this.draggedFieldGroupIndex === null || this.draggedFieldData === null) {
                return
            }

            // 如果是拖拽到同一分组，不需要处理
            if (this.draggedFieldGroupIndex === targetGroupIndex) {
                return
            }

            const sourceGroup = this.configData.fieldGroups[this.draggedFieldGroupIndex]
            const targetGroup = this.configData.fieldGroups[targetGroupIndex]

            // 从源分组中移除字段
            const fieldIndex = sourceGroup.fields.indexOf(this.draggedFieldData)
            if (fieldIndex > -1) {
                sourceGroup.fields.splice(fieldIndex, 1)
            }

            // 添加到目标分组
            if (!targetGroup.fields.includes(this.draggedFieldData)) {
                targetGroup.fields.push(this.draggedFieldData)
            }
        },

        // 字段拖拽结束
        handleFieldDragEnd() {
            this.draggedFieldGroupIndex = null
            this.draggedFieldIndex = null
            this.draggedFieldData = null
        }
    }
}
</script>

<style scoped>
/* 拖拽时的视觉反馈 */
.cursor-move {
    cursor: grab;
}

.cursor-move:active {
    cursor: grabbing;
}

/* 拖拽中的元素样式 */
.dragging {
    opacity: 0.5;
    transform: rotate(2deg);
}

/* 拖拽目标区域样式 */
.drag-over {
    background-color: var(--fallback-p,oklch(var(--p)/0.1));
    border-color: var(--fallback-p,oklch(var(--p)/0.3));
    border-style: dashed;
}

/* 字段拖拽到分组时的样式 */
.field-drag-over {
    border-color: var(--fallback-p,oklch(var(--p)/0.5)) !important;
    background-color: var(--fallback-p,oklch(var(--p)/0.1)) !important;
}

/* 徽章样式优化 */
.badge {
    @apply transition-all duration-200;
}

.badge-outline:hover {
    @apply bg-base-200 border-base-300;
}

/* 输入框样式优化 */
.input:focus {
    @apply border-primary shadow-sm;
}
</style>
